import React, { forwardRef } from 'react';
import {
  Checkbox as ChakraCheckbox,
  CheckboxProps as ChakraCheckboxProps,
  IconProps,
} from '@chakra-ui/react';
import { CheckOutlineIcon } from '@allergan-data-labs/alle-elements-icons/20/checkOutlineIcon';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
export interface CheckboxProps extends ChakraCheckboxProps {
  isChecked?: boolean;
  isIndeterminate?: boolean;
  isDisabled?: boolean;
  value?: string | number;
  /** 
    This prop determines which mode the component should display
    @default light
  */
  colorMode?: ColorMode;
}

/**
 * Checkbox component is used in forms when a user needs to select multiple values from several options.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (props, ref) => {
    const { children, colorMode: propColorMode, icon, ...otherProps } = props;
    const { colorMode } = useColorMode(propColorMode);
    /* 
      The colorScheme value is populated by the colorMode prop. Had to do it
      this way because we cannot extend the chakra-ui component props and
      we kept getting an error for using custom props on a chakra component.
      Keep in mind that not all of our themed library components require
      this method. We also could not use the colorScheme property for our color
      mode changes because certain components, like button, was already using
      the colorScheme property.
    */
    const colorScheme = colorMode;

    return (
      <ChakraCheckbox
        ref={ref}
        colorScheme={colorScheme}
        icon={icon ? icon : <CustomCheckboxIcon />}
        {...otherProps}
      >
        {children}
      </ChakraCheckbox>
    );
  }
);

interface CheckboxIconProps extends IconProps {
  /**
   * @default false
   */
  isIndeterminate?: boolean;
  /**
   * @default false
   */
  isChecked?: boolean;
}

const CustomCheckboxIcon = (props: CheckboxIconProps) => {
  const { isIndeterminate, isChecked, ...rest } = props;

  return isChecked || isIndeterminate ? <CheckOutlineIcon {...rest} /> : null;
};

export { CheckboxGroup } from '@chakra-ui/react';
